import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  formatMinor,
  parseMajor,
  PRICE_TYPES,
  useEffectivePrices,
  usePriceLists,
  useUpsertPriceEntry,
  type EffectivePriceRow,
  type PriceTypeKey,
} from '@/hooks/use-pricing';
import { useT } from '@/i18n/use-t';

type Draft = Record<PriceTypeKey, string>;

function emptyDraft(): Draft {
  return {
    unitPriceMinor: '',
    wholesalePriceMinor: '',
    minPriceMinor: '',
    maxPriceMinor: '',
    discountPriceMinor: '',
    vipPriceMinor: '',
  };
}

/**
 * Major-unit string of a price-type on an effective row, '' when unset. On an
 * item sold by the litre an entered fuel price shows with its three decimals
 * (RL-994); on any other item the retail price is its cents, so a leftover
 * fuel price can never block an edit of the item's other prices.
 */
function draftFromRow(row: EffectivePriceRow, soldByLitre: boolean): Draft {
  const d = emptyDraft();
  for (const { key } of PRICE_TYPES) {
    const v = row[key];
    d[key] = v === null ? '' : formatMinor(v);
  }
  const milli = row.unitPriceMilli ?? null;
  if (soldByLitre && milli !== null) d.unitPriceMinor = formatMinor(milli, 3);
  return d;
}

/** A read-only price: the retail column shows an entered fuel price at its three decimals. */
function priceText(row: EffectivePriceRow, key: PriceTypeKey): string {
  const milli = row.unitPriceMilli ?? null;
  if (key === 'unitPriceMinor' && milli !== null) return formatMinor(milli, 3);
  const value = row[key];
  return value === null ? '—' : formatMinor(value);
}

/**
 * RL-994: the retail price as sent. On an item sold by the litre, a value with
 * three decimals is the fuel price a till sells by: it goes as unitPriceMilli,
 * with its round-half-up cents price for every cents reader. Any other value is
 * a cents price, which clears a fuel price; elsewhere three decimals are
 * refused by `parseMajor`.
 */
function retailPrice(
  value: string,
  soldByLitre: boolean,
): { unitPriceMinor: string; unitPriceMilli: string | null } {
  if (soldByLitre && /\.\d{3}$/.test(value.trim())) {
    const milli = parseMajor(value, 3);
    return { unitPriceMinor: ((BigInt(milli) + 5n) / 10n).toString(), unitPriceMilli: milli };
  }
  return { unitPriceMinor: parseMajor(value), unitPriceMilli: null };
}

/**
 * RFL-202 — per-item prices by type. Retail (`unitPriceMinor`) is the
 * till sell-price; wholesale / min / max / discount are optional
 * Back-Office metadata. VIP (RFL-344) is read by re:form Loyalty for
 * eligible card categories — it is NOT applied by the till. One row per
 * active price list; Save upserts the whole set (blank optional fields
 * clear that type).
 */
export function ItemPricingTab({
  itemId,
  unitOfMeasure,
}: {
  itemId: string;
  unitOfMeasure?: string;
}) {
  const t = useT();
  const soldByLitre = unitOfMeasure === 'l';
  const lists = usePriceLists('active');
  const effective = useEffectivePrices(itemId);
  const upsert = useUpsertPriceEntry(itemId);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);

  // Seed drafts from effective rows once they load (don't clobber edits).
  useEffect(() => {
    if (effective.data === undefined) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const row of effective.data ?? []) {
        if (row.barcode !== null) continue;
        if (next[row.priceListId] === undefined) {
          next[row.priceListId] = draftFromRow(row, soldByLitre);
        }
      }
      return next;
    });
  }, [effective.data, soldByLitre]);

  if (lists.isLoading || effective.isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('catalog.pricing.loading', 'Loading prices…')}
      </p>
    );
  }

  const activeLists = lists.data ?? [];
  // This editor authors the SKU fallback. Barcode overrides share a list but
  // must never replace the fallback in this map merely because they were
  // returned later; they are ingested/managed as distinct YUKI-2 rows.
  const byList = new Map(
    effective.data?.filter((row) => row.barcode === null).map((row) => [row.priceListId, row]) ??
      [],
  );
  const barcodePrices = effective.data?.filter((row) => row.barcode !== null) ?? [];

  function setField(listId: string, key: PriceTypeKey, value: string): void {
    setDrafts((prev) => ({
      ...prev,
      [listId]: { ...(prev[listId] ?? emptyDraft()), [key]: value },
    }));
  }

  return (
    <div className="space-y-3">
      {activeLists.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t(
            'catalog.pricing.noLists',
            'No active price lists. Create one in the Pricing section first.',
          )}
        </p>
      )}
      {activeLists.length > 0 && (
        <div className="overflow-x-auto rounded-md border bg-background">
          <Table>
            <THead>
              <TR>
                <TH>{t('catalog.pricing.priceList', 'Price list')}</TH>
                <TH>{t('catalog.pricing.currency', 'Currency')}</TH>
                {PRICE_TYPES.map((priceType) => (
                  <TH key={priceType.key} className="w-28">
                    {t(priceType.labelKey, priceType.label)}
                  </TH>
                ))}
                <TH className="w-24" />
              </TR>
            </THead>
            <TBody>
              {activeLists.map((list) => {
                const existing = byList.get(list.id);
                const draft = drafts[list.id] ?? emptyDraft();
                const baseline =
                  existing === undefined ? emptyDraft() : draftFromRow(existing, soldByLitre);
                const dirty = PRICE_TYPES.some(({ key }) => draft[key] !== baseline[key]);
                return (
                  <TR key={list.id}>
                    <TD>
                      <span className="font-medium">{list.name}</span>{' '}
                      {list.isDefault && (
                        <Badge variant="success">{t('catalog.pricing.default', 'default')}</Badge>
                      )}
                    </TD>
                    <TD className="font-mono text-xs">{list.currencyCode}</TD>
                    {PRICE_TYPES.map(({ key }) => (
                      <TD key={key}>
                        <Input
                          value={draft[key]}
                          onChange={(e) => setField(list.id, key, e.target.value)}
                          placeholder={
                            key !== 'unitPriceMinor' ? '—' : soldByLitre ? '0.000' : '0.00'
                          }
                          inputMode="decimal"
                          className="w-24"
                        />
                      </TD>
                    ))}
                    <TD className="text-right">
                      <Button
                        size="sm"
                        variant={dirty ? 'default' : 'ghost'}
                        disabled={!dirty || upsert.isPending}
                        onClick={async () => {
                          setError(null);
                          try {
                            if (draft.unitPriceMinor.trim() === '') {
                              setError(
                                t('catalog.pricing.retailRequired', 'Retail price is required.'),
                              );
                              return;
                            }
                            const optional = (s: string): string | null =>
                              s.trim() === '' ? null : parseMajor(s);
                            await upsert.mutateAsync({
                              priceListId: list.id,
                              ...retailPrice(draft.unitPriceMinor, soldByLitre),
                              wholesalePriceMinor: optional(draft.wholesalePriceMinor),
                              minPriceMinor: optional(draft.minPriceMinor),
                              maxPriceMinor: optional(draft.maxPriceMinor),
                              discountPriceMinor: optional(draft.discountPriceMinor),
                              vipPriceMinor: optional(draft.vipPriceMinor),
                            });
                          } catch (err) {
                            setError(extractError(err));
                          }
                        }}
                      >
                        {t('common.save', 'Save')}
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>
      )}
      {barcodePrices.length > 0 && (
        <section className="space-y-2">
          <div>
            <h3 className="text-sm font-medium">
              {t('catalog.pricing.barcodePrices', 'Barcode prices')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t(
                'catalog.pricing.barcodeNote',
                'Imported variant prices are read-only and apply only to the listed EAN.',
              )}
            </p>
          </div>
          <div className="overflow-x-auto rounded-md border bg-background">
            <Table>
              <THead>
                <TR>
                  <TH>{t('catalog.common.barcode', 'Barcode')}</TH>
                  <TH>{t('catalog.pricing.priceList', 'Price list')}</TH>
                  <TH>{t('catalog.pricing.currency', 'Currency')}</TH>
                  {PRICE_TYPES.map((type) => (
                    <TH key={type.key}>{t(type.labelKey, type.label)}</TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {barcodePrices.map((row) => (
                  <TR key={`${row.priceListId}:${row.barcode ?? ''}`}>
                    <TD className="font-mono">{row.barcode}</TD>
                    <TD>{row.priceListName}</TD>
                    <TD className="font-mono text-xs">{row.currencyCode}</TD>
                    {PRICE_TYPES.map(({ key }) => (
                      <TD key={key}>{priceText(row, key)}</TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </section>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
